import {useCallback, useEffect, useState} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import Typography from 'components/Typography';
import {useQuery, QueryFunctionContext} from 'react-query';
import {format, parse, parseISO} from 'date-fns';
import {
  createAddress,
  Customer,
  DataItem,
  ListResponse,
  Request,
  SelectOption,
  UserAddress,
} from 'types';
import api from 'api';
import states from 'constants/states';
import {CustomerService, UpdateCustomerDto} from 'services/customer.service';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import ViewDetails from 'components/ViewDetails/ViewDetails';
import {
  mapMMStatusText,
  mapMMSubStatusColor,
  mapStatusColor,
  mapStatusText,
} from 'constants/mappings';
import {get} from 'lodash';
import useAuth from 'hooks/useAuth';
import toast from 'react-hot-toast';
import {phoneFormat} from 'helpers/phoneFormat';
import CheckIcon from 'pages/Settings/Users/CheckIcon';
import {TypeGooglePlaceDetail} from 'components/GooglePlaceAutoComplete';
import useSort from 'hooks/useSort';
import {CreditCardType} from 'components/CreditCardType';
import styles from './UserForm.module.scss';
interface Option {
  label: string;
  value: string | number;
}
interface FormValue {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  // phone_1?: string;
  phone_2?: string;
  phone_3?: string;
  birthday: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zipcode: string;
  state: Option;
  allergies: string;
}
interface PaginatedResponse<T> {
  count: number;
  limit: number;
  offset: number;
  results: T[];
}

async function getUserById({queryKey}: QueryFunctionContext) {
  const [, userId, isPrescriber] = queryKey;

  if (!userId || isPrescriber) return;

  const {data} = await api.get<Customer>(`/customers/${userId}/`);

  return data;
}

async function getUserCards({queryKey}: QueryFunctionContext) {
  const [, userId, isPrescriber] = queryKey;

  if (!userId || isPrescriber) return;

  const {data} = await api.get<any[]>(`/payment/method/?customer=${userId}`);

  return data;
}

async function getUserTransactions({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, pageSize, order, isPrescriber] = queryKey;

  if (!userId || isPrescriber) return;

  const {data} = await api.get<PaginatedResponse<any>>(
    `/payment/transactions/`,
    {
      params: {
        offset: +page * Number(pageSize),
        limit: Number(pageSize),
        customer_id: userId,
        ordering: order,
      },
    }
  );

  return data;
}
async function getUserOrders({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, pageSize] = queryKey;

  if (!userId) return;

  const {data} = await api.get<PaginatedResponse<any>>(`/requests/`, {
    params: {
      offset: +page * Number(pageSize),
      limit: Number(pageSize),
      source: 'micromerchant',
      customer_id: userId,
      order: '-id',
    },
  });

  return data;
}
async function getUserRequests({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, pageSize] = queryKey;

  if (!userId) return;

  const {data} = await api.get<PaginatedResponse<any>>(`/requests/`, {
    params: {
      offset: +page * Number(pageSize),
      limit: Number(pageSize),
      source: 'app',
      customer_id: userId,
      order: '-id',
    },
  });

  return data;
}

async function getFilledOrders({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, isPrescriber] = queryKey;

  if (isPrescriber) {
    return;
  }

  const {data} = await api.get<ListResponse<Request>>('/requests/', {
    params: {
      customer_id: userId,
      status: 'charge_all',
      source: 'micromerchant',
      limit: 100,
      is_paid: false,
    },
  });

  return data;
}

async function getStates({queryKey}: QueryFunctionContext<string[]>) {
  const [, keyWord, isPrescriber] = queryKey;

  if (isPrescriber) {
    return;
  }

  const {data} = await api.get<PaginatedResponse<any>>(
    '/address-information/states/',
    {
      params: {
        limit: 40,
        search: keyWord,
      },
    }
  );
  return data;
}

async function getCities({queryKey}: QueryFunctionContext<string[]>) {
  const [, keyWordCity, isPrescriber] = queryKey;

  if (isPrescriber) {
    return;
  }

  const {data} = await api.get<PaginatedResponse<any>>(
    '/address-information/cities/',
    {
      params: {
        limit: 20,
        search: keyWordCity,
      },
    }
  );
  return data;
}

type FormData = Omit<createAddress, 'label'>;

function useUserForm(user_id?: number) {
  const {userId} = useParams<{userId: string}>();
  const {push, location} = useHistory();
  const {
    control: addressControl,
    setValue: setAddressValee,
    handleSubmit: handleAddressSubmit,
    reset: resetAddress,
  } = useForm<FormData>();
  const [submitting, setSubmitting] = useState(false);
  const [keyWord, setKeyWord] = useState('');
  const [keyWordCity, setKeyWordCity] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(40);
  const [status, setStatus] = useState(
    sessionStorage.getItem('user_tab') || ''
  );
  const {sortBy, sortOrder, onSort, order} = useSort('transaction_id', 'desc');
  const [addressesForUpdate, setAddressesForUpdate] = useState<UserAddress[]>(
    []
  );

  const [copayItems, setCopayItems] = useState<any[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [data, setData] = useState<{
    preference_time: null | string;
    preference_date: null | Date;
  }>({
    preference_time: null,
    preference_date: null,
  });
  const [locationPreview, setLocationPreview] = useState({
    latitude: 40.7596958,
    longitude: -73.9650045,
  });
  const [deliveryAddressId, setDeliveryAddressId] = useState(0);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');
  const [isCharging, setIsCharging] = useState(false);

  const {currentUser, rolePermission} = useAuth();
  const isPrescriber = get(currentUser, 'role') === 'prescriber';

  const fetchable = location.pathname.startsWith('/users');

  function transformData(
    data: Request[],
    isRequest: boolean,
    source: 'app' | 'micro'
  ) {
    return data.map((item) => ({
      ...item,
      sync_time: get(item, 'sync_date', '') + ' ' + item.sync_time,
      number: <Typography variant="strong">#M{item.id}</Typography>,
      is_paid: item.is_paid ? 'Yes' : 'No',
      rx_no: `${item.rx_no || ''}${
        item.refill_no ? ` (${item.refill_no})` : ''
      }`,
      status:
        source !== 'app' ? (
          <StatusBadge
            /*// @ts-ignore*/
            color={mapMMSubStatusColor[get(item, 'status', '')]}
          >
            {/*// @ts-ignore*/}
            {mapMMStatusText[get(item, 'status', '')]}
          </StatusBadge>
        ) : (
          /*// @ts-ignore*/
          <StatusBadge color={mapStatusColor[get(item, 'status', '')]}>
            {/*// @ts-ignore*/}
            {mapStatusText[get(item, 'status', '')]}
          </StatusBadge>
        ),
      view: <ViewDetails to={`/requests/${item.id}`} />,
      checkbox: (
        <div
          className={styles.checkbox}
          onClick={(val) => {
            val.stopPropagation();
            const include = isRequest
              ? selectedOrders.find((i) => i === item.id)
              : copayItems.find((i) => i.id === item.prescription_id);

            if (include) {
              if (isRequest) {
                setSelectedOrders(selectedOrders.filter((i) => i !== item.id));
              } else {
                setCopayItems(
                  copayItems
                    .filter((i) => i.id !== item.prescription_id)
                    .map((i) => ({
                      id: i.id,
                      amount_due: i.amount_due,
                    }))
                );
              }
            } else {
              if (isRequest) {
                setSelectedOrders((pr) => [...pr, item.id]);
              } else {
                setCopayItems([
                  ...copayItems,
                  {id: item.prescription_id, amount_due: item.amount_due},
                ]);
              }
            }
          }}
        >
          <CheckIcon
            emptySelect={
              isRequest
                ? !selectedOrders.find((i) => i === item.id)
                : !copayItems.find((i) => i.id === item.prescription_id)
            }
            allSelected={
              isRequest
                ? selectedOrders.find((i) => i === item.id)
                : copayItems.find((i) => i.id === item.prescription_id)
            }
          />
        </div>
      ),
      preference_date:
        item.preference_date || item.preference_time
          ? `${item.preference_date || '--/--/--'} / ${
              item.preference_time || '-'
            }`
          : '',
    }));
  }

  useEffect(() => {
    setCurrentPage(1);
  }, [status]);

  const {
    handleSubmit,
    register,
    control,
    formState: {errors},
    reset,
    setValue,
  } = useForm<FormValue>();

  const {data: stateList} = useQuery(
    ['states', keyWord, isPrescriber ? 'yes' : ''],
    getStates,
    {
      enabled: fetchable,
    }
  );
  const {data: cityList} = useQuery(
    ['cities', keyWordCity, isPrescriber ? 'yes' : ''],
    getCities,
    {
      enabled: fetchable,
    }
  );
  const {
    data: customer,
    refetch,
    isLoading,
  } = useQuery(['user', userId, isPrescriber], getUserById);

  const [selectCardModal, setSelectCardModal] = useState(false);
  const [accessCardModal, setAccessCardModal] = useState(false);
  const [addressModalVisible, setAddressModalVisible] = useState(false);

  const [cardId, setCardIs] = useState<SelectOption>();

  const {data: cards, refetch: refetchCards} = useQuery(
    ['card', userId || user_id, isPrescriber],
    getUserCards,
    {
      enabled: fetchable || location.pathname.startsWith('/orders'),
    }
  );
  const {data: transactions, isLoading: transactionsLoading} = useQuery(
    [
      'transactions',
      userId || user_id,
      currentPage - 1,
      pageSize,
      order,
      isPrescriber,
    ],
    getUserTransactions,
    {
      enabled: fetchable,
    }
  );
  const {
    data: orders,
    isLoading: orderLoading,
    refetch: refetchOrders,
  } = useQuery(
    ['user_orders', userId || user_id, currentPage - 1, pageSize],
    getUserOrders,
    {
      enabled: fetchable,
    }
  );

  const {data: requests, isLoading: requestsLoading} = useQuery(
    ['user_requests', userId || user_id, currentPage - 1, pageSize],
    getUserRequests,
    {
      enabled: fetchable,
    }
  );

  const {data: filledOrders, refetch: refetchFilledOrders} = useQuery(
    ['user_filled_orders', userId || user_id, isPrescriber],
    getFilledOrders,
    {
      enabled: fetchable,
    }
  );

  // useEffect(() => {
  //   if (filledOrders?.results)
  //     setCopayItems(
  //       filledOrders?.results.map((item) => ({
  //         id: item.prescription_id,

  //         amount_due: item.amount_due,
  //       }))
  //     );
  // }, [filledOrders]);

  useEffect(() => {
    if (cards && cards.length > 0) {
      setCardIs({
        value: cards[0].custom_uuid,
        label: `**** **** **** ${get(cards[0], 'last4', '')} - ${
          cards[0].cardholder_name
        }`,
      });
    }
  }, [cards]);

  useEffect(() => {
    if (customer?.user) {
      reset({
        ...customer,
        ...customer.user,
        phone: phoneFormat(customer.user.phone),
        phone_2: customer?.phone_2 ? phoneFormat(customer.phone_2) : undefined,
        phone_3: customer?.phone_3 ? phoneFormat(customer.phone_3) : undefined,
        birthday: customer.birthday
          ? format(
              parse(customer.birthday, 'yyyy-MM-dd', new Date()),
              'MM/dd/yyyy'
            )
          : '',
        state: states.find((state) => state.value === customer.user.state),
      });
    }
  }, [customer, reset]);

  async function createNewAddress(newAddress: createAddress) {
    const data = await api.post<any>(`/customers/address/`, {
      customer_id: Number(userId),
      ...newAddress,
    });
    if (data.status === 200) {
      await refetch();
    }
    return data;
  }

  async function createNewCard(newCard: any) {
    const data = await api.post<any>(`/payment/card/`, newCard);
    refetchCards();
    return data;
  }

  async function deleteAddress(addressId: number | undefined) {
    const {data} = await api.delete<Customer>(
      `/customers/address/${addressId}/`
    );

    refetch();

    return data;
  }

  async function deleteCard(cardId: number | undefined) {
    const {data} = await api.delete<Customer>(`/payment/method/${cardId}/`);
    refetchCards();
    return data;
  }

  async function submit(data: FormValue) {
    console.log(data);
    // return;
    setSubmitting(true);

    const customerService = new CustomerService();

    if (customer) {
      const dto: UpdateCustomerDto = {
        ...data,
        phone: data.phone.replace(/\D/g, ''),
        birthday: format(
          parse(data.birthday, 'MM/dd/yyyy', new Date()),
          'yyyy-MM-dd'
        ),
        addresses: addressesForUpdate,
      };
      if (data?.phone_2 && data?.phone_2.trim() !== '') {
        dto.phone_2 = data?.phone_2?.replace(/\D/g, '');
      }
      if (data?.phone_3 && data?.phone_3.trim() !== '') {
        dto.phone_3 = data?.phone_3?.replace(/\D/g, '');
      }
      await customerService.update(customer.id, customer.user.id, dto);
      toast.success('Customer updated successfully');
      // await api.patch(`/users/${userId}/`, dto);
    } else {
      const user = {
        ...data,
        phone: data.phone.replace(/\D/g, ''),
        birthday: format(
          parse(data.birthday, 'MM/dd/yyyy', new Date()),
          'yyyy-MM-dd'
        ),
      };
      if (data?.phone_2 && data?.phone_2.trim() !== '') {
        user.phone_2 = data?.phone_2?.replace(/\D/g, '');
      }
      if (data?.phone_3 && data?.phone_3.trim() !== '') {
        user.phone_3 = data?.phone_3?.replace(/\D/g, '');
      }

      try {
        await api.post('/customers/', user);
      } catch (error) {
        const errors = get(error, 'response,data', {});
        const error_arr = Object.values(errors);
        toast.error(
          (get(error_arr, '[0]', '') as string) || 'Something went wrong'
        );
        setSubmitting(false);
      }
    }

    setSubmitting(false);
    if (!userId) {
      push(userId ? '/users' : '/users');
    }
    // push(userId ? `/settings/users/${userId}` : '/settings/users');
  }

  const returnType = (type: string) => {
    if (type === 'patient_app') {
      return 'Patient App';
    }
    if (type === 'pos') {
      return 'POS';
    }
    if (type === 'dashboard') {
      return 'Dashboard';
    }
    if (type === 'driver_app_cod') {
      return 'Driver App COD';
    }
    if (type === 'payment_link') {
      return 'Payment Link';
    }
  };

  const metas = {
    orders: {
      totalPages: Math.ceil((orders?.count || 0) / 20),
      count: orders?.count || 0,
    },
    requests: {
      totalPages: Math.ceil((requests?.count || 0) / 20),
      count: requests?.count || 0,
    },
    transactions: {
      totalPages: Math.ceil((transactions?.count || 0) / 20),
      count: transactions?.count || 0,
    },
  };

  const submitSelectedCard = async () => {
    if (copayItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    try {
      setIsCharging(true);
      await api.post('/payment/charge-all/', {
        customer: userId,
        payment_method_uuid: cardId?.value,
        copay_items: copayItems,
      });
      setAccessCardModal(false);
      refetchFilledOrders();
      setCopayItems([]);
      setAllSelected(false);
      toast.success('Success');
    } catch (error) {
      toast.error('Error try again');
    } finally {
      setIsCharging(false);
    }
  };

  const onCharge = async () => {
    // if (cards.length < 1) {
    //   toast.error('Please add billing card to user first');
    //   return;
    // }
    // if (cards.length > 1) {
    //   setSelectCardModal(true);
    //   return;
    // }
    setAccessCardModal(true);
  };

  const sendAppLink = async () => {
    try {
      await api.post(`/customers/${userId}/send-sms/`);
    } catch (error) {}
  };

  const syncHandler = async () => {
    try {
      await api.post(`/customers/${userId}/sync-with-mm/`);
      toast.success('Patient was synced successfully');
      refetch();
    } catch (error) {}
  };

  const handleNavigateTransaction = useCallback(
    (row: DataItem) => {
      if (get(row, 'service_name', '') === 'CreditSale') {
        push(`/transactions/${row.id}`);
      }
    },
    [push]
  );

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      push(`/requests/${row.id}`);
    },
    [push]
  );

  const handleNavigateOrders = useCallback(
    (row: DataItem) => {
      push(`/orders/${row.id}`);
    },
    [push]
  );

  const onChangeStatus = (val: any) => {
    setOrderStatus(val?.value);
  };

  function onChangeTime(value: any) {
    setData(value);
  }

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setAddressValee('latitude', placeDetail.lat);
    setAddressValee('longitude', placeDetail.lon);
    setAddressValee('zipcode', placeDetail.zipcode);
    setAddressValee('address_line_1', placeDetail.addressLine1);
    setAddressValee('city', placeDetail.city);
    setAddressValee('state', placeDetail.state);
    setTimeout(() => {
      setLocationPreview({
        latitude: placeDetail.lat,
        longitude: placeDetail.lon,
      });
    }, 300);
  };

  const handleAddressCreation = async (datas: createAddress) => {
    const res = await createNewAddress({
      ...datas,
      customer_id: customer?.id,
    });

    if (get(res, 'status') === 200) {
      refetch();
      setOpenAddressModal(false);
      setDeliveryAddressId(res.data.id);
      resetAddress();
      setLocationPreview({
        latitude: 40.7596958,
        longitude: -73.9650045,
      });
    } else {
      toast.error('Something wrong with creation. Try again later !');
    }
  };

  const submitOrderStatutes = async () => {
    try {
      await api.post('/requests/requests/bulk-update/', {
        request_ids: selectedOrders,
        status: orderStatus,
        preference_date: data.preference_date
          ? format(data.preference_date, 'yyyy-MM-dd')
          : undefined,
        preference_time: data.preference_time || undefined,
        delivery_address_id: deliveryAddressId,
      });
      refetchOrders();
      setShowStatusModal(false);
      setOrderStatus('');
      setSelectedOrders([]);
      setData({
        preference_date: null,
        preference_time: null,
      });

      setDeliveryAddressId(0);
      toast.success('Successfully updated statutes!');
    } catch (error) {
      toast.error(
        JSON.stringify(get(error, 'response.data')) ||
          'Something wrong with creation. Try again later !'
      );
    }
  };

  const canWrite = rolePermission['patients']?.permissions?.write;

  return {
    userId,
    customer,
    addresses: customer?.addresses || [],
    cards: cards || [],
    orders: orders?.results
      ? transformData(orders?.results, true, 'micro')
      : [],
    requests: requests?.results
      ? transformData(requests?.results, true, 'app')
      : [],
    filledOrders: filledOrders?.results
      ? transformData(filledOrders?.results, false, 'micro')
      : [],
    submitting,
    errors,
    control,
    register,
    reset,
    setValue,
    isLoading,
    orderLoading,
    requestsLoading,
    transactionsLoading,
    addressesForUpdate,
    setAddressesForUpdate,
    handleSubmit: handleSubmit(submit),
    createNewAddress,
    createNewCard,
    deleteAddress,
    deleteCard,
    setKeyWord,
    setKeyWordCity,
    stateList,
    cityList,
    refetch,
    transactions:
      transactions?.results.map((item) => ({
        ...item,
        id: item.transaction_id,
        amount: '$' + item.amount,
        transaction_origin: returnType(item.transaction_origin),
        transaction_date: format(
          parseISO(item.transaction_date),
          'MM/dd/yy EEE hh:mm a'
        ),
        list_rx: `${item.list_rx.slice(0, 3).join(', ')} ${
          item.list_rx.length > 3 ? '...' : ''
        }`,
        list_orders: `${get(item, 'list_orders', []).slice(0, 3).join(', ')} ${
          item?.list_orders?.length > 3 ? '...' : ''
        }`,
        card_type: item?.card_type && <CreditCardType type={item.card_type} />,
      })) || [],
    setCurrentPage,
    currentPage,
    pageSize,
    setPageSize,
    status,
    setStatus,
    totalPages: metas[status as keyof typeof metas]?.totalPages,
    count: metas[status as keyof typeof metas]?.count,
    isPrescriber,
    copayItems,
    submitSelectedCard,
    onCharge,
    accessCardModal,
    setAccessCardModal,
    selectCardModal,
    setSelectCardModal,
    addressModalVisible,
    setAddressModalVisible,
    cardId,
    setCardIs,
    sendAppLink,
    syncHandler,
    handleNavigateTransaction,
    handleNavigateRequest,
    handleNavigateOrders,
    canWrite: !!canWrite,
    selectedOrders,
    setSelectedOrders,
    allSelected,
    setAllSelected,
    showStatusModal,
    setShowStatusModal,
    onChangeStatus,
    onChangeTime,
    data,
    setData,
    deliveryAddressId,
    setDeliveryAddressId,
    openAddressModal,
    setOpenAddressModal,
    addressControl,
    setAddressValee,
    handleAddressSubmit,
    resetAddress,
    onSelectPlace,
    locationPreview,
    handleAddressCreation,
    push,
    orderStatus,
    setOrderStatus,
    submitOrderStatutes,
    sortBy,
    sortOrder,
    onSort,
    setCopayItems,
    isCharging,
  };
}

export default useUserForm;
